import React, { useEffect, useRef, useState } from 'react';

/**
 * LazyImage
 * - Only starts loading the real `src` once the element is within
 *   `rootMargin` of the viewport (via IntersectionObserver), or immediately
 *   if `inView` is passed as true.
 * - Shows `fallback` if the real image fails to load.
 * - Reserves space with `ratio` (width / height) so there's no layout shift
 *   while the image loads.
 * - Fades in once loaded.
 *
 * Props:
 *   src        - the real image URL
 *   fallback   - shown if `src` fails to load (or as a placeholder look)
 *   alt        - alt text
 *   ratio      - aspect ratio as width/height, e.g. 16/9 (default 16/9)
 *   className  - extra classes applied to the <img>
 *   inView     - optional: if true, skip the observer and load immediately
 */
export function LazyImage({
  src,
  fallback,
  alt = '',
  ratio = 16 / 9,
  className = '',
  inView: forceInView = false,
  ...props
}) {
  const containerRef = useRef(null);
  const [isInView, setIsInView] = useState(forceInView);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (forceInView || isInView) return;

    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      // No IntersectionObserver support (or no node) — just load it.
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [forceInView, isInView]);

  const resolvedSrc = hasError && fallback ? fallback : src;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-lg bg-muted"
      style={{ aspectRatio: `${ratio}` }}
    >
      {!isLoaded && (
        <div className="absolute inset-0 animate-pulse bg-muted" />
      )}
      {isInView && (
        <img
          src={resolvedSrc}
          alt={alt}
          loading="lazy"
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            setHasError(true);
            setIsLoaded(true);
          }}
          className={`h-full w-full object-cover transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          } ${className}`}
          {...props}
        />
      )}
    </div>
  );
}

export default LazyImage;